import { useState, useEffect } from 'react'
import { Plus, Trash2, Edit3, Check, FileText, Loader2, Calendar, Globe, RefreshCw, ChevronDown, ChevronRight, Brain } from 'lucide-react'
import { transcriptsApi, hubspotExcerptsApi, type DealTranscript, type HubspotExcerpt } from '@api/client'
import { showToast } from '@store/uiStore'

interface TranscriptSourcePanelProps {
  dealId: number | null
  isActive: boolean
  selectedTranscriptIds: Set<number>
  onToggleTranscript: (id: number) => void
  onTranscriptsLoaded: (transcripts: DealTranscript[]) => void
  includeExcerpt: boolean
  onToggleExcerpt: (v: boolean) => void
  hubspotExcerpt: HubspotExcerpt | null
  onExcerptChange: (e: HubspotExcerpt | null) => void
  excerptLoading: boolean
  onExcerptLoadingChange: (v: boolean) => void
}

export function TranscriptSourcePanel({
  dealId,
  isActive,
  selectedTranscriptIds,
  onToggleTranscript,
  onTranscriptsLoaded,
  includeExcerpt,
  onToggleExcerpt,
  hubspotExcerpt,
  onExcerptChange,
  excerptLoading,
  onExcerptLoadingChange,
}: TranscriptSourcePanelProps) {
  const [transcripts, setTranscripts] = useState<DealTranscript[]>([])
  const [transcriptsLoading, setTranscriptsLoading] = useState(false)

  // Inline add state
  const [adding, setAdding] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0])
  const [newText, setNewText] = useState('')
  const [saving, setSaving] = useState(false)

  // Inline edit state
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDate, setEditDate] = useState('')
  const [editText, setEditText] = useState('')
  const [expandedId, setExpandedId] = useState<number | null>(null)

  // Excerpt generate state
  const [excerptGenerating, setExcerptGenerating] = useState(false)

  // Brain summary state
  const [brainSummary, setBrainSummary] = useState<string | null>(null)
  const [brainSummaryGenerating, setBrainSummaryGenerating] = useState(false)
  const [brainSummaryExpanded, setBrainSummaryExpanded] = useState(false)

  // Fetch transcripts
  useEffect(() => {
    if (!isActive || !dealId) return
    setTranscriptsLoading(true)
    transcriptsApi.list(dealId).then((res) => {
      if (res.success && res.data) {
        setTranscripts(res.data)
        onTranscriptsLoaded(res.data)
      }
    }).finally(() => setTranscriptsLoading(false))
  }, [isActive, dealId])

  // Fetch HubSpot excerpt
  useEffect(() => {
    if (!isActive || !dealId) return
    onExcerptLoadingChange(true)
    hubspotExcerptsApi.get(dealId).then((res) => {
      if (res.success && res.data) onExcerptChange(res.data)
      else onExcerptChange(null)
    }).catch(() => onExcerptChange(null)).finally(() => onExcerptLoadingChange(false))
  }, [isActive, dealId])

  const handleAdd = async () => {
    if (!dealId || !newText.trim() || !newDate) return
    setSaving(true)
    const res = await transcriptsApi.create(dealId, {
      title: newTitle.trim() || `Call on ${newDate}`,
      callDate: newDate,
      transcript: newText.trim(),
    })
    if (res.success && res.data) {
      const updated = [res.data, ...transcripts]
      setTranscripts(updated)
      onTranscriptsLoaded(updated)
      setAdding(false)
      setNewTitle('')
      setNewDate(new Date().toISOString().split('T')[0])
      setNewText('')
    }
    setSaving(false)
  }

  const handleUpdate = async (id: number) => {
    if (!dealId) return
    setSaving(true)
    const res = await transcriptsApi.update(dealId, id, {
      title: editTitle.trim(),
      callDate: editDate,
      transcript: editText.trim(),
    })
    if (res.success && res.data) {
      const updated = transcripts.map((t) => (t.id === id ? res.data! : t))
      setTranscripts(updated)
      onTranscriptsLoaded(updated)
      setEditingId(null)
    }
    setSaving(false)
  }

  const handleDelete = async (id: number) => {
    if (!dealId || !confirm('Delete this transcript? This cannot be undone.')) return
    const res = await transcriptsApi.remove(dealId, id)
    if (res.success) {
      const updated = transcripts.filter((t) => t.id !== id)
      setTranscripts(updated)
      onTranscriptsLoaded(updated)
      if (expandedId === id) setExpandedId(null)
      if (editingId === id) setEditingId(null)
    }
  }

  const startEdit = (t: DealTranscript) => {
    setEditingId(t.id)
    setEditTitle(t.title)
    setEditDate(t.call_date)
    setEditText(t.transcript)
    setExpandedId(t.id)
  }

  const handleGenerateExcerpt = async () => {
    if (!dealId) return
    setExcerptGenerating(true)
    try {
      const res = await hubspotExcerptsApi.generate(dealId)
      if (res.success && res.data) {
        onExcerptChange(res.data)
        onToggleExcerpt(true)
        showToast('HubSpot excerpt generated', 'success')
      } else {
        showToast(res.error || 'Failed to generate excerpt', 'error')
      }
    } catch {
      showToast('Network error', 'error')
    }
    setExcerptGenerating(false)
  }

  const handleGenerateBrainSummary = async () => {
    if (!dealId || selectedTranscriptIds.size === 0) return
    setBrainSummaryGenerating(true)
    try {
      const res = await transcriptsApi.generateBrainSummary(dealId, Array.from(selectedTranscriptIds))
      if (res.success && res.data) {
        setBrainSummary(res.data.summary)
        setBrainSummaryExpanded(true)
        showToast(`Brain summary generated from ${res.data.transcript_count} transcript${res.data.transcript_count !== 1 ? 's' : ''}`, 'success')
      } else {
        showToast(res.error || 'Failed to generate Brain summary', 'error')
      }
    } catch {
      showToast('Network error', 'error')
    }
    setBrainSummaryGenerating(false)
  }

  return (
    <>
      {/* Transcripts section */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> Saved Transcripts
            {transcripts.length > 0 && (
              <span className="text-[9px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] px-1.5 py-0.5 rounded">{transcripts.length}</span>
            )}
          </p>
          {dealId && !adding && (
            <button
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-blue-400 bg-blue-500/10 border border-blue-500/25 rounded hover:bg-blue-500/20 transition-colors"
            >
              <Plus className="w-3 h-3" /> Add transcript
            </button>
          )}
        </div>

        {transcriptsLoading && (
          <p className="text-[11px] text-[var(--text-muted)] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading transcripts...</p>
        )}

        {/* Inline add form */}
        {adding && (
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Title (e.g. Discovery call with John)"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="flex-1 px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
              />
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-[var(--text-muted)]" />
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="px-2 py-1.5 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)]"
                />
              </div>
            </div>
            <textarea
              placeholder="Paste the transcript, call notes, or meeting summary..."
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y font-mono"
              rows={6}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setAdding(false); setNewTitle(''); setNewText('') }}
                className="px-2.5 py-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={saving || !newText.trim() || !newDate}
                className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                Save
              </button>
            </div>
          </div>
        )}

        {/* Transcript list */}
        {transcripts.length > 0 ? (
          <div className="grid gap-1.5">
            {transcripts.map((t) => {
              const selected = selectedTranscriptIds.has(t.id)
              const wordCount = t.transcript.split(/\s+/).filter(Boolean).length
              const isEditing = editingId === t.id
              const isExpanded = expandedId === t.id

              return (
                <div
                  key={t.id}
                  className={`rounded-lg border transition-colors ${
                    selected
                      ? 'border-violet-500/40 bg-violet-500/10'
                      : 'border-[var(--border-color)] bg-[var(--bg-primary)]'
                  }`}
                >
                  <div className="flex items-center gap-2.5 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => onToggleTranscript(t.id)}
                      className="rounded border-[var(--border-color)]"
                    />
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : t.id)}
                      className="flex-1 min-w-0 flex items-center gap-2 text-left"
                    >
                      {isExpanded ? <ChevronDown className="w-3 h-3 text-[var(--text-muted)] shrink-0" /> : <ChevronRight className="w-3 h-3 text-[var(--text-muted)] shrink-0" />}
                      <span className="text-xs font-medium text-[var(--text-primary)] truncate">{t.title}</span>
                      <span className="text-[10px] text-[var(--text-muted)] shrink-0">{t.call_date}</span>
                    </button>
                    <span className="text-[10px] text-[var(--text-muted)] shrink-0">{wordCount.toLocaleString()} words</span>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        onClick={() => isEditing ? setEditingId(null) : startEdit(t)}
                        className="p-1 text-[var(--text-muted)] hover:text-blue-400 transition-colors"
                        title="Edit"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded view / edit */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-[var(--border-color)]">
                      {isEditing ? (
                        <div className="space-y-2 pt-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              className="flex-1 px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]"
                            />
                            <input
                              type="date"
                              value={editDate}
                              onChange={(e) => setEditDate(e.target.value)}
                              className="px-2 py-1.5 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]"
                            />
                          </div>
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] resize-y font-mono"
                            rows={8}
                          />
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setEditingId(null)} className="px-2.5 py-1 text-xs text-[var(--text-secondary)]">
                              Cancel
                            </button>
                            <button
                              onClick={() => handleUpdate(t.id)}
                              disabled={saving}
                              className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg disabled:opacity-50"
                            >
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <pre className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap font-mono pt-2 max-h-48 overflow-y-auto">{t.transcript}</pre>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : !transcriptsLoading && !adding ? (
          <p className="text-[11px] text-[var(--text-muted)] italic py-1">No transcripts yet — click "Add transcript" to add one.</p>
        ) : null}
      </div>

      {/* Brain Summary section */}
      {dealId && transcripts.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide flex items-center gap-1.5">
              <Brain className="w-3 h-3" /> Frankie Brain Summary
            </p>
            <button
              onClick={handleGenerateBrainSummary}
              disabled={brainSummaryGenerating || selectedTranscriptIds.size === 0}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded hover:bg-violet-500/20 transition-colors disabled:opacity-50"
            >
              {brainSummaryGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
              {brainSummaryGenerating ? 'Summarizing...' : brainSummary ? 'Regenerate' : `Summarize ${selectedTranscriptIds.size} transcript${selectedTranscriptIds.size !== 1 ? 's' : ''}`}
            </button>
          </div>
          {brainSummary ? (
            <div className="rounded-lg border border-violet-500/30 bg-violet-500/5">
              <button
                onClick={() => setBrainSummaryExpanded(!brainSummaryExpanded)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left"
              >
                {brainSummaryExpanded ? <ChevronDown className="w-3 h-3 text-violet-400 shrink-0" /> : <ChevronRight className="w-3 h-3 text-violet-400 shrink-0" />}
                <span className="text-xs font-medium text-violet-400">Brain-generated deal summary</span>
                <span className="text-[10px] text-[var(--text-muted)] ml-auto">{brainSummary.split(/\s+/).filter(Boolean).length.toLocaleString()} words</span>
              </button>
              {brainSummaryExpanded && (
                <div className="px-3 pb-3 border-t border-violet-500/20">
                  <pre className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap font-sans leading-relaxed pt-2 max-h-64 overflow-y-auto">{brainSummary}</pre>
                </div>
              )}
            </div>
          ) : !brainSummaryGenerating ? (
            <p className="text-[11px] text-[var(--text-muted)] italic py-1">
              Select transcripts above and click &ldquo;Summarize&rdquo; to generate a structured deal summary using Frankie Brain.
            </p>
          ) : null}
        </div>
      )}

      {/* HubSpot Excerpt section */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide flex items-center gap-1.5">
            <Globe className="w-3 h-3" /> HubSpot Excerpt
          </p>
          {hubspotExcerpt && dealId && (
            <button
              onClick={handleGenerateExcerpt}
              disabled={excerptGenerating}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-blue-400 bg-blue-500/10 border border-blue-500/25 rounded hover:bg-blue-500/20 transition-colors disabled:opacity-50"
            >
              {excerptGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Refresh
            </button>
          )}
        </div>
        {excerptLoading ? (
          <p className="text-[11px] text-[var(--text-muted)] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading excerpt...</p>
        ) : hubspotExcerpt ? (
          <label
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
              includeExcerpt
                ? 'border-violet-500/40 bg-violet-500/10'
                : 'border-[var(--border-color)] bg-[var(--bg-primary)] hover:bg-[var(--bg-tertiary)]'
            }`}
          >
            <input
              type="checkbox"
              checked={includeExcerpt}
              onChange={() => onToggleExcerpt(!includeExcerpt)}
              className="rounded border-[var(--border-color)]"
            />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium text-[var(--text-primary)]">AI-condensed HubSpot activity brief</span>
              <span className="text-[10px] text-[var(--text-muted)] ml-2">{hubspotExcerpt.activity_count} activities · {new Date(hubspotExcerpt.generated_at).toLocaleDateString()}</span>
            </div>
            <span className="text-[10px] text-[var(--text-muted)] shrink-0">{hubspotExcerpt.excerpt.split(/\s+/).filter(Boolean).length.toLocaleString()} words</span>
          </label>
        ) : (
          <div className="flex items-center justify-between py-1">
            <p className="text-[11px] text-[var(--text-muted)] italic">
              {dealId ? 'No excerpt generated yet.' : 'Link a deal first to enable HubSpot excerpts.'}
            </p>
            {dealId && (
              <button
                onClick={handleGenerateExcerpt}
                disabled={excerptGenerating}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium text-blue-400 bg-blue-500/10 border border-blue-500/25 rounded hover:bg-blue-500/20 transition-colors disabled:opacity-50"
              >
                {excerptGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {excerptGenerating ? 'Generating...' : 'Generate excerpt'}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  )
}
